import { useMemo, useState } from 'react'
import { todayIso } from './reportDates'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** "2026-10" for a date, from the date's own parts. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7)
}

/** The first and last day of a "2026-10" month. */
export function monthBounds(month: string): { from: string; to: string } {
  const [year, number] = month.split('-').map(Number)
  const last = new Date(Date.UTC(year, number, 0)).getUTCDate()
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` }
}

export function monthLabel(month: string): string {
  const [year, number] = month.split('-').map(Number)
  return `${MONTH_NAMES[number - 1]} ${year}`
}

function shiftMonth(month: string, offset: number): string {
  const [year, number] = month.split('-').map(Number)
  const moved = new Date(Date.UTC(year, number - 1 + offset, 1))
  return `${moved.getUTCFullYear()}-${String(moved.getUTCMonth() + 1).padStart(2, '0')}`
}

/** Next month, this month, and the eleven before it, most recent first. */
export function monthChoices(today: string): { value: string; label: string }[] {
  const current = monthOf(today)
  return Array.from({ length: 13 }, (_, index) => {
    const value = shiftMonth(current, 1 - index)
    return { value, label: monthLabel(value) }
  })
}

/**
 * The date range of the monthly reports: one calendar month, chosen from
 * the months around today, opening on the current one.
 */
export function useMonthRange() {
  const [month, setMonth] = useState(() => monthOf(todayIso()))
  const choices = useMemo(() => monthChoices(todayIso()), [])
  const { from, to } = monthBounds(month)
  return { month, choices, from, to, invalid: false, setMonth }
}

export type MonthRange = ReturnType<typeof useMonthRange>
